import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Eye, EyeOff, Loader2, Search } from "lucide-react";
import { api, fileUrl, PLACEHOLDER_IMG } from "@/lib/api";
import { CATEGORY_MAP } from "@/data/categories";
import { toast } from "sonner";
import ProductDialog from "./ProductDialog";

export default function ProductsAdmin({ onChange }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/products", { params: { include_inactive: true } });
      setProducts(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (CATEGORY_MAP[p.category]?.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleActive = async (p) => {
    try {
      await api.put(`/products/${p.id}`, { is_active: !p.is_active });
      toast.success(p.is_active ? "Producto ocultado" : "Producto activado");
      load();
      onChange?.();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Producto eliminado");
      load();
      onChange?.();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <div className="bg-white border-2 border-slate-950">
      <div className="border-b-2 border-slate-950 p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div>
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600">Gestión</div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Productos</h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="products-search"
              className="pl-9 pr-3 py-2.5 border-2 border-slate-300 focus:border-slate-950 focus:outline-none text-sm w-full sm:w-64"
            />
          </div>
          <button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="add-product-btn"
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-5 py-2.5 text-xs btn-brutal inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> Nuevo producto
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-16 text-center text-slate-500">
          <p className="font-display uppercase tracking-tight">No hay productos</p>
          <p className="text-sm mt-1">Comienza agregando tu primer producto al catálogo.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b-2 border-slate-950">
              <tr className="text-left font-display font-bold uppercase text-xs tracking-wider">
                <th className="p-4">Imagen</th>
                <th className="p-4">Producto</th>
                <th className="p-4">Categoría</th>
                <th className="p-4">Estado</th>
                <th className="p-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const img = p.images?.[0] ? fileUrl(p.images[0].storage_path) : PLACEHOLDER_IMG;
                return (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="p-3">
                      <div className="w-16 h-16 border border-slate-200 bg-slate-100 overflow-hidden">
                        <img src={img} alt={p.name} onError={(e)=>{e.currentTarget.src=PLACEHOLDER_IMG;}} className="w-full h-full object-cover" />
                      </div>
                    </td>
                    <td className="p-4 font-display font-bold text-slate-950">{p.name}</td>
                    <td className="p-4 text-slate-600 font-mono text-xs uppercase tracking-wider">{CATEGORY_MAP[p.category]?.name || p.category}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-display font-bold uppercase tracking-wider ${p.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                        {p.is_active ? "Activo" : "Oculto"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleActive(p)} data-testid={`toggle-active-${p.id}`} title={p.is_active ? "Ocultar" : "Activar"} className="p-2 hover:bg-slate-100 text-slate-700">
                          {p.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`edit-product-${p.id}`} title="Editar" className="p-2 hover:bg-slate-100 text-slate-700">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} title="Eliminar" className="p-2 hover:bg-red-50 text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProductDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={editing}
        onSaved={() => { load(); onChange?.(); }}
      />
    </div>
  );
}
